import {
    OrderStepFields,
} from 'app/Apps/OrderManagement/Orders/orders.types'
import {
    LocationType,
    TransportModeSea,
    SeaFreightCarrier,
    OrderTransportModeRoad,
    DefaultTruckingComment,
    DeliveryOptionRoad, SeaFreightContainerSize,
} from 'app/enums/label.enums'
import {
    FieldInCard, NestedField,
} from 'app/types/form.types'

type Args = {
    seaFreightContainerSize40Feet: boolean,
    seaFreightContainerSize20Feet: boolean,
    forwarderId: number,
    transportModeRoad: OrderTransportModeRoad,
    pickupLocationId: number,
    pickupLocationContacts: {id: number}[],
    deliveryLocationId: number,
    filterLocationType: LocationType,
    transportModeSea: TransportModeSea,
    isFirstStep: boolean,
    getAllowedValues: (enumType: Record<string, string>) => Record<string, string>,
    freightRateServiceProviderCode: string,
    seaFreightContainerSize: SeaFreightContainerSize,
    deliveryOptionRoad: DeliveryOptionRoad,
}

const getInternalSeaFormFields = ({
    seaFreightContainerSize,
    forwarderId,
    transportModeRoad,
    pickupLocationId,
    deliveryLocationId,
    filterLocationType,
    transportModeSea,
    getAllowedValues,
    freightRateServiceProviderCode,
    deliveryOptionRoad,
}: Args): FieldInCard<OrderStepFields>[] => {
    const trucksAndVans: FieldInCard<OrderStepFields>[] = [
        {
            name: 'deliveryOptionRoad',
            componentName: 'EnumSelector',
            allowedValues: getAllowedValues(DeliveryOptionRoad),
            className: 'deliveryOptionRoad',
            disabled: true,
            labelKey: 'OrderStep.deliveryOptionRoad',
        },
        ...deliveryOptionRoad === DeliveryOptionRoad.TRUCKS ? [{
            name: 'numberOfTrucks',
            componentName: 'IntegerInput',
            className: 'numberOfVehicles',
            required: true,
            labelKey: 'OrderStep.numberOfTrucks',
        }] as FieldInCard<OrderStepFields>[] : [],
        ...deliveryOptionRoad === DeliveryOptionRoad.VANS ? [{
            name: 'numberOfVans',
            componentName: 'IntegerInput',
            className: 'numberOfVehicles',
            required: true,
            labelKey: 'OrderStep.numberOfVans',
        }] as FieldInCard<OrderStepFields>[] : [],
    ]

    const seaFreightContainerSizeFields: NestedField<OrderStepFields>[] = [
        {
            name: 'seaFreightContainerSize',
            componentName: 'EnumSelector',
            allowedValues: getAllowedValues(SeaFreightContainerSize),
            noSort: true,
            required: true,
            labelKey: 'OrderStep.seaFreightContainerSize',
        },
        ...(seaFreightContainerSize === SeaFreightContainerSize.TWENTY_FEET)
        || (seaFreightContainerSize === SeaFreightContainerSize.TWENTY_AND_FORTY_FEET) ? [{
            name: 'numberOf20FeetContainers',
            componentName: 'IntegerInput',
            required: true,
            className: seaFreightContainerSize === SeaFreightContainerSize.TWENTY_AND_FORTY_FEET ? 'deliveryOptionRoad' : null,
            labelKey: 'OrderStep.numberOf20FeetContainers',
        }] as NestedField<OrderStepFields>[] : [],
        ...(seaFreightContainerSize === SeaFreightContainerSize.FORTY_FEET)
        || (seaFreightContainerSize === SeaFreightContainerSize.TWENTY_AND_FORTY_FEET) ? [{
            name: 'numberOf40FeetContainers',
            componentName: 'IntegerInput',
            className: seaFreightContainerSize === SeaFreightContainerSize.TWENTY_AND_FORTY_FEET ? 'numberOfVehicles' : null,
            required: true,
            labelKey: 'OrderStep.numberOf40FeetContainers',
        }] as NestedField<OrderStepFields>[] : [],
    ]

    return [
        {
            id: 'column1',
            fields: [
                {
                    name: 'forwarder',
                    componentName: 'FreightForwarderSelector',
                    freightRateServiceProviderCode,
                    required: true,
                    labelKey: 'OrderStep.forwarder',
                },
                {
                    name: 'forwarderContacts',
                    componentName: 'MultipleContactByLocationObjectSelector',
                    existedForm: true,
                    selectedLocation: forwarderId,
                    labelKey: 'OrderStep.forwarderContacts',
                },
                {
                    name: 'transportModeSea',
                    componentName: 'EnumSelector',
                    allowedValues: getAllowedValues(TransportModeSea),
                    labelKey: 'OrderStep.transportModeSea',
                    disabled: true,
                },
                ...transportModeSea === TransportModeSea.FCL ? seaFreightContainerSizeFields : [],
                {
                    name: 'transportModeRoad',
                    componentName: 'EnumSelector',
                    allowedValues: getAllowedValues(OrderTransportModeRoad),
                    labelKey: 'OrderStep.transportModeRoad',
                    disabled: true,
                },
                ...transportModeRoad === OrderTransportModeRoad.FTL ? trucksAndVans as [] : [],
                {
                    name: 'isTemperatureControlled',
                    componentName: 'Checkbox',
                    labelKey: 'OrderStep.isTemperatureControlled',
                    disabled: true,
                },
                {
                    name: 'defaultTruckingComment',
                    componentName: 'EnumSelector',
                    allowedValues: getAllowedValues(DefaultTruckingComment),
                    useDropdown: true,
                    labelKey: 'OrderStep.defaultTruckingComment',
                    disabled: true,
                },
                {
                    name: 'truckingComment',
                    componentName: 'LongText',
                    className: 'truckingComment',
                    labelKey: 'OrderStep.truckingComment',
                    disabled: true,
                },
            ],
        },
        {
            id: 'column2',
            fields: [
                {
                    name: 'pickupLocation',
                    componentName: 'LocationObjectSelector',
                    isShippingLocation: true,
                    labelKey: 'OrderStep.pickupLocation',
                    disabled: true,
                },
                {
                    name: 'pickupLocationContacts',
                    componentName: 'MultipleContactByLocationObjectSelector',
                    existedForm: true,
                    selectedLocation: pickupLocationId,
                    labelKey: 'OrderStep.pickupLocationContacts',
                },
                {
                    name: 'requestedPickup',
                    componentName: 'DateTimeZonedFromLocation',
                    locationId: pickupLocationId,
                    labelKey: 'OrderStep.requestedPickup',
                },
                {
                    name: 'estimatedPickup',
                    componentName: 'DateTimeZonedFromLocation',
                    locationId: pickupLocationId,
                    labelKey: 'OrderStep.estimatedPickup',
                },
                {
                    name: 'pickupComment',
                    componentName: 'LongText',
                    labelKey: 'OrderStep.pickupComment',
                    disabled: true,
                },
            ],
        },
        {
            id: 'column3',
            fields: [
                {
                    name: 'deliveryLocation',
                    componentName: 'LocationObjectSelector',
                    isShippingLocation: true,
                    labelKey: 'OrderStep.deliveryLocation',
                    disabled: true,
                },
                {
                    name: 'deliveryLocationContacts',
                    componentName: 'MultipleContactByLocationObjectSelector',
                    existedForm: true,
                    selectedLocation: deliveryLocationId,
                    labelKey: 'OrderStep.deliveryLocationContacts',
                },
                {
                    name: 'requestedDelivery',
                    componentName: 'DateTimeZonedFromLocation',
                    locationId: deliveryLocationId,
                    labelKey: 'OrderStep.requestedDelivery',
                },
                {
                    name: 'estimatedDelivery',
                    componentName: 'DateTimeZonedFromLocation',
                    locationId: deliveryLocationId,
                    labelKey: 'OrderStep.estimatedDelivery',
                },
                {
                    name: 'deliveryComment',
                    componentName: 'LongText',
                    labelKey: 'OrderStep.deliveryComment',
                },
            ],
        },
        {
            id: 'column4',
            fields: [
                {
                    name: 'seaFreightCarrier',
                    componentName: 'EnumSelector',
                    allowedValues: getAllowedValues(SeaFreightCarrier),
                    labelKey: 'OrderStep.seaFreightCarrier',
                    disabled: true,
                },
                {
                    name: 'originSeaport',
                    componentName: 'LocationObjectSelector',
                    filterLocationType,
                    labelKey: 'OrderStep.originSeaport',
                    disabled: true,
                },
                {
                    name: 'destinationSeaport',
                    componentName: 'LocationObjectSelector',
                    filterLocationType,
                    labelKey: 'OrderStep.destinationSeaport',
                    disabled: true,
                },
                {
                    name: 'seaFreightComment',
                    componentName: 'LongText',
                    labelKey: 'OrderStep.seaFreightComment',
                },
            ],
        },
    ]
}

export default getInternalSeaFormFields
